import { useMediaQuery } from '@mui/material';
import { useAuthOptional } from '../contexts/AuthContext';
// From the split module, not the provider: importing the provider here pulls
// RevaPanel and the app route table into anything that uses PropertyMap,
// including the marketing build.
import { useRevaOptional } from '../contexts/revaContextValue';

/**
 * Where Reva sits on screen.
 *
 * - `docked` — a column inside the app shell; the page reflows around it, so the
 *              conversation and the page it is about are readable at the same
 *              time, and collapsing leaves a narrow rail rather than nothing.
 * - `sheet`  — the full-screen conversation, unchanged from the phone layout.
 */
export type RevaPlacement = 'docked' | 'sheet';

/** Width of the rail the docked column collapses to. */
export const REVA_RAIL_WIDTH = 56;

/** Below this viewport width Reva takes the whole screen, as it always has. */
export const REVA_SHEET_MAX_WIDTH = 900;
/** At or above this viewport width the page can spare the wider column. */
export const REVA_WIDE_MIN_WIDTH = 1200;

/**
 * The docked column's width per viewport band. Reva only takes the wider column
 * once the page can afford it: on a 900–1199px screen the content beside it
 * would stop being usable, so the panel narrows instead.
 */
export const REVA_DOCKED_WIDTH = { narrow: 360, wide: 480 } as const;

/** The docked column's width at the current viewport. */
export const useRevaDockedWidth = (): number => {
  const isWide = useMediaQuery(`(min-width:${REVA_WIDE_MIN_WIDTH}px)`);
  return isWide ? REVA_DOCKED_WIDTH.wide : REVA_DOCKED_WIDTH.narrow;
};

/**
 * The single placement rule every Reva surface reads. Viewport alone decides it:
 * every desktop width docks, including the admin tools, so the page never
 * disappears behind the conversation.
 */
export const useRevaPlacement = (): RevaPlacement => {
  const isSheet = useMediaQuery(`(max-width:${REVA_SHEET_MAX_WIDTH - 1}px)`);
  return isSheet ? 'sheet' : 'docked';
};

/**
 * Whether Reva claims permanent room in the app shell — the open column or the
 * collapsed rail. Signed-out visitors have no way to open the conversation, so
 * they get neither, and accounts Reva never serves are excluded outright.
 */
export const useRevaShellDocked = (): boolean => {
  const placement = useRevaPlacement();
  // Both are absent on the marketing site, where PropertyMap renders without
  // the app's providers. No Reva and no signed-in user means never docked.
  const reva = useRevaOptional();
  const auth = useAuthOptional();

  return Boolean(reva?.available) && Boolean(auth?.currentUser?.id) && placement === 'docked';
};

/**
 * Horizontal space the app shell must leave on the right for Reva: the full
 * column while it is open, the rail once it is collapsed, nothing otherwise.
 */
export const useRevaShellOffset = (): number => {
  const shellDocked = useRevaShellDocked();
  const dockedWidth = useRevaDockedWidth();
  // Absent outside the app shell — the marketing site renders PropertyMap with
  // no RevaProvider. No Reva means no space to reserve for it.
  const reva = useRevaOptional();

  if (!reva) return 0;

  if (!shellDocked) return 0;
  return reva.isOpen ? dockedWidth : REVA_RAIL_WIDTH;
};

export default useRevaPlacement;
